import {
  EditableValue,
  ExternalSourceSystem,
  ModernizedActivityCreationSource,
  ModernizedCourseContentSectionType,
  ModernizedQualityIndicator,
  ModernizedScoreCategory,
  ModernizedActivityEntityType,
  ModernizedActivityStatus,
  type GetModernizedCourseQuery,
  type ModernizedAssessment,
} from "@axio/graphql/generated/graphql";

import type {
  ActivityDetail,
  ActivityIssueFinding,
  ActivityRecommendationSource,
  CourseEvaluatorDimensionScore,
  CourseEvaluatorReviewCategory,
  CourseEvaluatorReviewDocumentIssue,
  CourseEvaluatorReviewEffectiveness,
  CourseEvaluatorReviewItemKind,
  CourseEvaluatorReviewData,
  CourseModernizerReviewTeamManagement,
  CourseModernizerReviewUser,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
  CourseEvaluatorReviewSidebarMeta,
  CourseEvaluatorReviewSidebarTag,
  CourseEvaluatorReviewItemType,
  PublishDocumentUpdate,
} from "./types/reviewTypes";
import { createActivityAttachment } from "./utils/attachments";

type ReviewCourse = NonNullable<GetModernizedCourseQuery["getModernizedCourse"]>;
type ReviewModule = ReviewCourse["modules"][number];
type ReviewAssignment = ReviewModule["assignments"][number];
type ReviewAssessment = ReviewModule["assessments"][number] &
  Pick<ModernizedAssessment, "recommendedTitle" | "recommendedDescription">;
type ReviewAxioResource = ReviewModule["axioResources"][number];
type ReviewProject = ReviewModule["projects"][number];
type ReviewOtherActivity = ReviewModule["otherActivities"][number];
type ReviewIleRecommendation = ReviewModule["ileRecommendations"][number];
type ReviewContentSection = ReviewCourse["contentSections"][number];
type ReviewModernizerIntake = NonNullable<ReviewCourse["modernizerIntake"]>;
type ReviewRecommendation = {
  id?: string | null;
  recommendation?: string | null;
  rationale?: string | null;
  sourceType?: ActivityRecommendationSource["sourceType"] | null;
  traceOrigin?: string | null;
  traceGenerator?: string | null;
  traceSignals?: ReadonlyArray<string | null> | null;
};
type ReviewModernizerUser =
  | NonNullable<ReviewModernizerIntake["architect"]>
  | ReviewModernizerIntake["editingTeamConnection"]["edges"][number]["node"];

export function isManuallyAuthoredActivity(
  creationSource:
    | ReviewAssignment["creationSource"]
    | ReviewProject["creationSource"]
    | ReviewOtherActivity["creationSource"]
    | ReviewIleRecommendation["creationSource"]
    | null
    | undefined
): boolean {
  return creationSource === ModernizedActivityCreationSource.ManualAuthoring;
}

type ReviewIleObjective = NonNullable<ReviewIleRecommendation["aiTutor"]>["objectives"][number];

export type ReviewDataBundle = {
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
};

const SCORE_CATEGORY_LABELS: Record<ModernizedScoreCategory, string> = {
  [ModernizedScoreCategory.CourseDesign]: "Course Design",
  [ModernizedScoreCategory.Assessment]: "Assessments",
  [ModernizedScoreCategory.Engagement]: "Engagement",
  [ModernizedScoreCategory.Accessibility]: "Accessibility",
};

const SOURCE_LABELS: Record<ExternalSourceSystem, string> = {
  [ExternalSourceSystem.Brightspace]: "Brightspace",
  [ExternalSourceSystem.Canvas]: "Canvas",
  [ExternalSourceSystem.Imscc]: "IMSCC",
  [ExternalSourceSystem.Pdf]: "PDF",
  [ExternalSourceSystem.Docx]: "DOCX",
};

const QUALITY_TO_EFFECTIVENESS: Record<ModernizedQualityIndicator, CourseEvaluatorReviewEffectiveness> = {
  [ModernizedQualityIndicator.Excellent]: "excellent",
  [ModernizedQualityIndicator.Good]: "good",
  [ModernizedQualityIndicator.Fair]: "fair",
  [ModernizedQualityIndicator.NeedsRevision]: "poor",
};

const CONTENT_SECTION_TITLES: Record<ModernizedCourseContentSectionType, string> = {
  [ModernizedCourseContentSectionType.LearningObjectives]: "Learning Objectives",
  [ModernizedCourseContentSectionType.CourseMaterialsList]: "Course Materials List",
  [ModernizedCourseContentSectionType.InstructorAvailability]: "Instructor Availability",
  [ModernizedCourseContentSectionType.Other]: "Other",
};

const BRIGHTSPACE_SYNTHETIC_UNASSIGNED_MODULE_EXTERNAL_ID = "brightspace:synthetic:unassigned";

const TARGET_AUDIENCE_LABELS: Record<string, string> = {
  TRADITIONAL_STUDENT: "Traditional student",
  WORKING_PROFESSIONAL: "Working professional",
  GRADUATE_STUDENT: "Graduate student",
  MIXED: "Mixed",
};

function resolveCourseTargetAudience(course: ReviewCourse): string | undefined {
  const targetAudience = course.targetAudience;
  if (!targetAudience) {
    return undefined;
  }

  return TARGET_AUDIENCE_LABELS[targetAudience] ?? targetAudience;
}

function firstNonBlank(...values: Array<string | null | undefined>): string {
  return values.find((value) => Boolean(value?.trim())) ?? "";
}

function stripHtmlTags(value: string | null | undefined): string | undefined {
  const stripped = value
    ?.replace(/<[^>]*>/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
  return stripped || undefined;
}

function firstNonBlankDescription(text: string | null | undefined, html: string | null | undefined): string {
  return firstNonBlank(text, stripHtmlTags(html), html);
}

function isSyntheticBrightspaceUnassignedModule(module: ReviewModule): boolean {
  return module.externalModuleId === BRIGHTSPACE_SYNTHETIC_UNASSIGNED_MODULE_EXTERNAL_ID;
}

function shouldAddModuleDescriptionItem(
  module: ReviewModule,
  visibleOtherActivities: ReviewOtherActivity[],
  visibleIleRecommendations: ReviewIleRecommendation[]
): boolean {
  if (isSyntheticBrightspaceUnassignedModule(module)) {
    return false;
  }

  if (module.isNew !== true) {
    return true;
  }

  return (
    module.assignments.length > 0 ||
    module.assessments.length > 0 ||
    module.axioResources.length > 0 ||
    module.projects.length > 0 ||
    visibleOtherActivities.length > 0 ||
    visibleIleRecommendations.length > 0
  );
}

function mapActivityTypeToItemType(activityType: string | null | undefined): CourseEvaluatorReviewItemType {
  switch (activityType) {
    case "moduleDescription":
      return "moduleDescription";
    case "discussion":
      return "discussion";
    case "checklist":
      return "checklist";
    case "externalLink":
      return "externalLink";
    case "quiz":
      return "quiz";
    case "file":
      return "file";
    case "resource":
      return "resource";
    case "html-content":
      return "htmlContent";
    case "page":
      return "page";
    case "webpage":
      return "webpage";
    case "link":
      return "link";
    case "ile":
      return "ile";
    default:
      return "moduleDescription";
  }
}

function mapIssueFindingsDetailed(
  issueFindings?: ReadonlyArray<string> | null,
  issueFindingDimensions?: ReadonlyArray<string> | null,
  issueFindingDimensionLabels?: ReadonlyArray<string> | null
): ActivityIssueFinding[] | undefined {
  if (!issueFindings?.length) {
    return undefined;
  }

  return issueFindings.map((text, index) => ({
    text,
    dimension: issueFindingDimensions?.[index] ?? undefined,
    dimensionLabel: issueFindingDimensionLabels?.[index] ?? undefined,
  }));
}

const SUGGESTED_FINDING_LIMIT = 2;

const ISSUE_CONTEXT_STOP_WORDS = new Set([
  "about",
  "above",
  "actual",
  "add",
  "after",
  "again",
  "against",
  "all",
  "also",
  "and",
  "are",
  "before",
  "being",
  "below",
  "between",
  "but",
  "can",
  "cannot",
  "course",
  "details",
  "does",
  "each",
  "for",
  "from",
  "has",
  "have",
  "into",
  "itself",
  "learners",
  "module",
  "must",
  "needs",
  "only",
  "other",
  "page",
  "provided",
  "required",
  "should",
  "specific",
  "students",
  "that",
  "the",
  "their",
  "there",
  "these",
  "this",
  "those",
  "through",
  "using",
  "visible",
  "where",
  "which",
  "will",
  "with",
  "without",
  "work",
  "you",
  "your",
]);

const TOPIC_ONLY_ISSUE_CATEGORIES = new Set(["dashboard_visualization", "data_cleaning"]);

function normalizeIssueContext(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenizeIssueContext(value: string | null | undefined): Set<string> {
  return new Set(
    normalizeIssueContext(value)
      .split(" ")
      .filter((token) => token.length > 2 && !ISSUE_CONTEXT_STOP_WORDS.has(token))
  );
}

function buildSuggestedIssueContext(values: Array<string | null | undefined>): string {
  return values
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
    .join(" ");
}

function stripGeneratedLogisticsTail(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }

  const sectionStart = trimmed.search(
    /\b(estimated time|materials and navigation|how to complete|completion requirements|what to submit|academic integrity|academic integrity and feedback|participation and support|accessibility and support|accessibility|support)\s*:/i
  );
  if (sectionStart > 0) {
    return trimmed.slice(0, sectionStart).trim();
  }

  const paragraphEnd = trimmed.search(/\n\s*\n/);
  if (paragraphEnd > 0) {
    return trimmed.slice(0, paragraphEnd).trim();
  }

  return trimmed;
}

function buildSuggestedInstructionalContext(values: Array<string | null | undefined>): string {
  return buildSuggestedIssueContext(values.map(stripGeneratedLogisticsTail));
}

function getRecommendationContext(recommendationSummary: {
  feedback?: string;
  recommendationSources: ActivityRecommendationSource[];
}): string {
  return buildSuggestedIssueContext([
    recommendationSummary.feedback,
    ...recommendationSummary.recommendationSources.flatMap((source) => [
      source.recommendation,
      source.rationale,
      ...(source.traceSignals ?? []),
    ]),
  ]);
}

function getDimensionText(finding: ActivityIssueFinding): string {
  return normalizeIssueContext(`${finding.dimension ?? ""} ${finding.dimensionLabel ?? ""}`);
}

function getIssueMatchCategories(value: string): Set<string> {
  const normalizedValue = normalizeIssueContext(value);
  const categories = new Set<string>();

  if (/\b(dashboard|visualization|visualisation|chart|display|wireframe)\b/.test(normalizedValue)) {
    categories.add("dashboard_visualization");
  }
  if (/\b(data cleaning|cleaning|missing data|missing value|missing prices|rows)\b/.test(normalizedValue)) {
    categories.add("data_cleaning");
  }
  if (/\b(rubric|scoring|criteria|grading|point value|score|scores)\b/.test(normalizedValue)) {
    categories.add("rubric_criteria");
  }
  if (
    /\b(academic integrity|academic conduct|plagiarism|honesty|conduct expectations|own work)\b/.test(normalizedValue)
  ) {
    categories.add("academic_integrity");
  }
  if (
    /\b(staged|preparation|prepare|before|sequence|readiness|checkpoint|low stakes|knowledge check|practice check|practice|prerequisite)\b/.test(
      normalizedValue
    )
  ) {
    categories.add("staged_readiness");
  }
  if (
    /\b(feedback release|release rules|feedback timing|when feedback|scores will be released)\b/.test(normalizedValue)
  ) {
    categories.add("feedback_release");
  }
  if (
    /\b(due date|availability|attempts|time limit|quiz settings|lms settings|submit|submission|file format)\b/.test(
      normalizedValue
    )
  ) {
    categories.add("activity_configuration");
  }
  if (
    /\b(accommodation|accommodations|accessibility services|support resources|student support|captions|transcripts|accessible formats|access information)\b/.test(
      normalizedValue
    )
  ) {
    categories.add("accessibility_support");
  }
  if (/\b(contact|support|services|resources|policy|policies|netiquette|communication)\b/.test(normalizedValue)) {
    categories.add("support_contact");
  }
  if (
    /\b(color contrast|contrast|labeling|reading order|screen reader|keyboard|usability|accessibility expectations|accessible design|user testing)\b/.test(
      normalizedValue
    )
  ) {
    categories.add("accessibility_practice");
  }
  if (
    /\b(ai|authentic|verifiable|evidence|original|process artifact|self reported|generic response)\b/.test(
      normalizedValue
    )
  ) {
    categories.add("ai_vulnerability");
  }
  if (/\b(objective|outcome|align|mapping|competency|orphaned|unaddressed)\b/.test(normalizedValue)) {
    categories.add("objective_alignment");
  }

  return categories;
}

function getMatchedIssueCategories(findingCategories: Set<string>, contextCategories: Set<string>): Set<string> {
  const matchedCategories = new Set<string>();

  findingCategories.forEach((category) => {
    if (contextCategories.has(category) && !TOPIC_ONLY_ISSUE_CATEGORIES.has(category)) {
      matchedCategories.add(category);
    }
  });

  return matchedCategories;
}

function getContextDimensionHints(suggestionContext: string): string[] {
  const normalizedContext = normalizeIssueContext(suggestionContext);
  const hints: string[] = [];

  if (
    /\b(rubric|scoring|criteria|grade|grading|point|assignment|project|submission|feedback|quiz|assessment)\b/.test(
      normalizedContext
    )
  ) {
    hints.push("rubric alignment");
  }
  if (/\b(ai|authentic|evidence|verifiable|original|process|reflection|discussion|claim)\b/.test(normalizedContext)) {
    hints.push("ai vulnerability");
  }
  if (/\b(objective|outcome|align|mapping|competency|skill|practice|lab|ile|interactive)\b/.test(normalizedContext)) {
    hints.push("content objective mapping");
  }
  if (
    /\b(instruction|purpose|deliverable|navigation|sequence|schedule|deadline|due|checkpoint|readiness|overview|guide|support|contact|policy|expectation|pacing)\b/.test(
      normalizedContext
    )
  ) {
    hints.push("cognitive load");
  }
  if (/\b(accessibility|accommodation|screen|reader|keyboard|contrast|alt)\b/.test(normalizedContext)) {
    hints.push("accessibility");
  }
  if (/\b(policy|academic|integrity|conduct|plagiarism|institution)\b/.test(normalizedContext)) {
    hints.push("institutional guidelines");
  }
  if (/\b(stale|outdated|deprecated|missing data|missing value|current|superseded)\b/.test(normalizedContext)) {
    hints.push("stale concepts", "stale content");
  }

  return [...new Set(hints)];
}

function scoreSuggestedIssueFinding(
  finding: ActivityIssueFinding,
  suggestionTokens: Set<string>,
  dimensionHints: string[],
  contextCategories: Set<string>
): number {
  const findingText = `${finding.text} ${finding.dimension ?? ""} ${finding.dimensionLabel ?? ""}`;
  const findingTokens = tokenizeIssueContext(findingText);
  const dimensionText = getDimensionText(finding);
  const findingCategories = getIssueMatchCategories(findingText);
  const matchedCategories = getMatchedIssueCategories(findingCategories, contextCategories);
  let score = 0;

  suggestionTokens.forEach((token) => {
    if (findingTokens.has(token)) {
      score += 2;
    }
  });

  if (
    (findingCategories.has("accessibility_support") || findingCategories.has("support_contact")) &&
    !contextCategories.has("accessibility_support") &&
    !contextCategories.has("support_contact")
  ) {
    return 0;
  }

  if (matchedCategories.size === 0) {
    return 0;
  }

  score += matchedCategories.size * 8;

  dimensionHints.forEach((hint) => {
    if (dimensionText.includes(hint)) {
      score += 6;
    }
  });

  return score;
}

function selectRelevantSuggestedIssueFindings(
  module: ReviewModule,
  suggestionContext: string
): ActivityIssueFinding[] | undefined {
  const moduleIssueFindingsDetailed = mapIssueFindingsDetailed(
    module.issueFindings,
    module.issueFindingDimensions,
    module.issueFindingDimensionLabels
  );

  if (!moduleIssueFindingsDetailed?.length) {
    return undefined;
  }

  const suggestionTokens = tokenizeIssueContext(suggestionContext);
  const dimensionHints = getContextDimensionHints(suggestionContext);
  const contextCategories = getIssueMatchCategories(suggestionContext);
  const scoredFindings = moduleIssueFindingsDetailed
    .map((finding, index) => ({
      finding,
      index,
      score: scoreSuggestedIssueFinding(finding, suggestionTokens, dimensionHints, contextCategories),
    }))
    .filter(({ score }) => score > 0)
    .sort((left, right) => right.score - left.score || left.index - right.index);

  if (scoredFindings.length === 0) {
    return undefined;
  }

  return scoredFindings.slice(0, SUGGESTED_FINDING_LIMIT).map(({ finding }) => finding);
}

function getSuggestedItemIssueFindings(
  module: ReviewModule,
  isNew: boolean | null | undefined,
  suggestionContext: string,
  issueFindings?: ReadonlyArray<string> | null,
  issueFindingDimensions?: ReadonlyArray<string> | null,
  issueFindingDimensionLabels?: ReadonlyArray<string> | null
): {
  issueFindings?: string[];
  issueFindingsDetailed?: ActivityIssueFinding[];
} {
  const ownIssueFindings = issueFindings ? [...issueFindings] : undefined;
  const ownIssueFindingsDetailed = mapIssueFindingsDetailed(
    issueFindings,
    issueFindingDimensions,
    issueFindingDimensionLabels
  );

  if (ownIssueFindingsDetailed?.length || !isNew) {
    return {
      issueFindings: ownIssueFindings,
      issueFindingsDetailed: ownIssueFindingsDetailed,
    };
  }

  const selectedModuleIssueFindingsDetailed = selectRelevantSuggestedIssueFindings(module, suggestionContext);

  return {
    issueFindings: selectedModuleIssueFindingsDetailed?.map((finding) => finding.text),
    issueFindingsDetailed: selectedModuleIssueFindingsDetailed,
  };
}

function mapQualityToEffectiveness(
  quality: ModernizedQualityIndicator | null | undefined
): CourseEvaluatorReviewEffectiveness | undefined {
  if (!quality) return undefined;
  return QUALITY_TO_EFFECTIVENESS[quality];
}

function getLastModified(
  editLogs: Array<{ created?: string | null; actor?: { name?: string | null; username?: string | null } | null }>
) {
  const latest = [...editLogs].sort((left, right) => {
    const leftValue = left.created ? new Date(left.created).getTime() : 0;
    const rightValue = right.created ? new Date(right.created).getTime() : 0;
    return rightValue - leftValue;
  })[0];

  if (!latest) {
    return { lastModifiedBy: undefined, lastModifiedAt: undefined };
  }

  return {
    lastModifiedBy: latest.actor?.name ?? latest.actor?.username ?? "System",
    lastModifiedAt: latest.created
      ? new Date(latest.created).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : undefined,
  };
}

function getLatestEditLogAfterValue(
  editLogs: Array<{ created?: string | null; fieldName?: EditableValue | null; afterValue?: string | null }>,
  fieldName: EditableValue
): string | undefined {
  const latest = editLogs
    .filter((editLog) => editLog.fieldName === fieldName)
    .sort((left, right) => {
      const leftValue = left.created ? new Date(left.created).getTime() : 0;
      const rightValue = right.created ? new Date(right.created).getTime() : 0;
      return rightValue - leftValue;
    })[0];

  return latest?.afterValue ?? undefined;
}

function mapRecommendationSources(
  recommendations: ReadonlyArray<ReviewRecommendation>
): ActivityRecommendationSource[] {
  return recommendations.flatMap((recommendation, index) => {
    const recommendationText = recommendation.recommendation?.trim();
    const rationale = recommendation.rationale?.trim();
    const traceSignals = (recommendation.traceSignals ?? [])
      .map((signal) => signal?.trim())
      .filter((signal): signal is string => Boolean(signal));

    if (!recommendationText && !rationale && traceSignals.length === 0) {
      return [];
    }

    const source: ActivityRecommendationSource = {
      id: recommendation.id ?? `recommendation-${index}`,
      recommendation: recommendationText || undefined,
      rationale: rationale || undefined,
      sourceType: recommendation.sourceType ?? undefined,
    };

    if (recommendation.traceOrigin) {
      source.traceOrigin = recommendation.traceOrigin;
    }
    if (recommendation.traceGenerator) {
      source.traceGenerator = recommendation.traceGenerator;
    }
    if (traceSignals.length > 0) {
      source.traceSignals = traceSignals;
    }

    return [source];
  });
}

function getRecommendationSummary(recommendations: ReadonlyArray<ReviewRecommendation>) {
  const recommendationSources = mapRecommendationSources(recommendations);
  const primary = recommendationSources[0];

  return {
    feedback: primary?.rationale,
    recommendedDescription: undefined,
    recommendationSources,
  };
}

function getContentSectionFeedback(recommendations: ReadonlyArray<ReviewRecommendation>): string | undefined {
  const rationaleParagraphs = recommendations
    .map((recommendation) => recommendation.rationale?.trim())
    .filter((rationale): rationale is string => Boolean(rationale));

  if (rationaleParagraphs.length === 0) {
    return undefined;
  }

  return [...new Set(rationaleParagraphs)].join("\n\n");
}

function mapGeneratedAssetsToAttachments(
  generatedAssets?: ReadonlyArray<{ id: string; fileName: string; fileURL?: string | null }> | null,
  originalFiles?: ReadonlyArray<{ id: string; fileName: string; fileURL?: string | null }> | null
) {
  const generated = (generatedAssets ?? []).map((asset) => ({
    ...createActivityAttachment({ id: asset.id, fileName: asset.fileName, url: asset.fileURL }),
    origin: "generated" as const,
  }));

  const original = (originalFiles ?? [])
    .filter((file) => file.fileURL)
    .map((file) => ({
      ...createActivityAttachment({ id: file.id, fileName: file.fileName, url: file.fileURL }),
      origin: "original" as const,
    }));

  const combined = [...original, ...generated];
  return combined.length > 0 ? combined : undefined;
}

function buildMeta({
  itemType,
  isNew,
  isManuallyAuthored,
  qualityIndicator,
  tag,
}: {
  itemType?: CourseEvaluatorReviewItemType;
  isNew?: boolean | null;
  isManuallyAuthored?: boolean;
  qualityIndicator?: ModernizedQualityIndicator | null;
  tag?: CourseEvaluatorReviewSidebarTag;
}): CourseEvaluatorReviewSidebarMeta[] | undefined {
  const meta: CourseEvaluatorReviewSidebarMeta[] = [];

  if (itemType && itemType !== "moduleDescription") {
    meta.push({ kind: "itemType", itemType });
  }

  if (tag) {
    meta.push({ kind: "tag", tag });
  }

  if (isNew) {
    meta.push({ kind: "tag", tag: "new" });
  }

  if (isManuallyAuthored) {
    meta.push({ kind: "manual" });
  }

  const effectiveness = mapQualityToEffectiveness(qualityIndicator);
  if (effectiveness) {
    meta.push({ kind: "effectiveness", effectiveness });
  }

  return meta.length > 0 ? meta : undefined;
}

function toCategoryBreakdown(modernizedCourse: ReviewCourse): CourseEvaluatorReviewCategory[] {
  return modernizedCourse.categoryScores.map((score) => ({
    name: SCORE_CATEGORY_LABELS[score.category] ?? score.category,
    score: score.score ?? 0,
    maxScore: score.maxScore ?? 0,
  }));
}

function toEvaluatorDimensionScores(modernizedCourse: ReviewCourse): CourseEvaluatorDimensionScore[] {
  return modernizedCourse.evaluatorDimensionScores.map((dimensionScore) => ({
    id: dimensionScore.id,
    dimension: dimensionScore.dimension,
    score: dimensionScore.score,
    maxScore: dimensionScore.maxScore,
    issueCount: dimensionScore.issueCount,
    summary: dimensionScore.summary,
    detailedFindings: dimensionScore.detailedFindings,
    evaluatedEntities: dimensionScore.evaluatedEntities,
    failedEntities: dimensionScore.failedEntities,
    goodEntities: dimensionScore.goodEntities,
  }));
}

function toDocumentIssues(modernizedCourse: ReviewCourse): CourseEvaluatorReviewDocumentIssue[] {
  return modernizedCourse.inaccessibleDocuments.map((doc) => ({
    id: doc.id,
    title: doc.title ?? "Unknown document",
    message: doc.errorReason ?? "Document could not be accessed",
    documentIssueKind: doc.errorReason?.toLowerCase().includes("authentication") ? "permissionDenied" : "brokenLink",
  }));
}

function toDocumentUpdates(modernizedCourse: ReviewCourse): PublishDocumentUpdate[] {
  if (!modernizedCourse.syllabusFile) {
    return [];
  }

  return [
    {
      id: modernizedCourse.syllabusFile.id,
      title: modernizedCourse.syllabusFile.fileName,
      reason: "Add this updated syllabus document to the LMS course.",
      externalUrl: null,
      downloadUrl: modernizedCourse.syllabusFile.fileURL,
    },
  ];
}

function addContentSectionDetail(details: Record<string, ActivityDetail>, section: ReviewContentSection): void {
  const lastModified = getLastModified(section.editLogs);

  details[section.id] = {
    id: section.id,
    currentTitle: "",
    currentDescription: section.valueText ?? section.valueHtml ?? "",
    recommendedTitle: CONTENT_SECTION_TITLES[section.type] ?? section.type,
    recommendedDescription: section.recommendedValue ?? "",
    activityType: "recommendation",
    feedback: getContentSectionFeedback(section.recommendations),
    recommendationSources: undefined,
    documentReference: "Course Overview module",
    attachments: mapGeneratedAssetsToAttachments(section.generatedAssets, section.originalFiles),
    ...lastModified,
  };
}

function addModuleDescriptionItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule
): void {
  const itemId = module.id;

  items.push({
    id: itemId,
    title: module.title ?? "Untitled Module",
    kind: "moduleDescription",
    entityType: ModernizedActivityEntityType.Module,
    status: module.status,
    pendingDelete: module.pendingDelete ?? false,
    externalVisibility: module.externalVisibility ?? undefined,
    meta: buildMeta({ itemType: "moduleDescription", isNew: module.isNew, qualityIndicator: module.qualityIndicator }),
  });

  const lastModified = getLastModified(module.editLogs ?? []);

  details[itemId] = {
    id: itemId,
    currentTitle: module.title ?? "",
    currentDescription: firstNonBlank(module.description),
    recommendedTitle: module.recommendedTitle ?? module.title ?? "",
    recommendedDescription: firstNonBlank(module.recommendedDescription),
    activityType: "moduleDescription",
    feedback: undefined,
    issueFindings: module.issueFindings ?? undefined,
    issueFindingsDetailed: mapIssueFindingsDetailed(
      module.issueFindings,
      module.issueFindingDimensions,
      module.issueFindingDimensionLabels
    ),
    documentReference: `Content > ${module.title ?? "Module"}`,
    attachments: mapGeneratedAssetsToAttachments(module.generatedAssets, module.originalFiles),
    ...lastModified,
  };
}

function addAssignmentItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  assignment: ReviewAssignment
): void {
  const gradingItemType = mapActivityTypeToItemType(assignment.gradingType);
  const tag =
    assignment.gradingType === "file" ? "lecture" : assignment.gradingType === "link" ? "supplementary" : undefined;
  const recommendationSummary = getRecommendationSummary(assignment.recommendations);
  const lastModified = getLastModified(assignment.editLogs);
  const isManuallyAuthored = isManuallyAuthoredActivity(assignment.creationSource);
  const itemType = isManuallyAuthored || gradingItemType === "moduleDescription" ? "assignment" : gradingItemType;
  const kind: CourseEvaluatorReviewItemKind = assignment.isNew ? "suggestedContent" : "assignment";
  const issueSummary = getSuggestedItemIssueFindings(
    module,
    assignment.isNew,
    buildSuggestedIssueContext([
      "assignment",
      assignment.gradingType,
      assignment.title,
      assignment.recommendedTitle,
      buildSuggestedInstructionalContext([
        assignment.recommendedDescription,
        firstNonBlankDescription(assignment.descriptionText, assignment.descriptionHtml),
      ]),
      getRecommendationContext(recommendationSummary),
    ]),
    assignment.issueFindings,
    assignment.issueFindingDimensions,
    assignment.issueFindingDimensionLabels
  );

  items.push({
    id: assignment.id,
    title: assignment.title ?? "Untitled Activity",
    kind,
    entityType: ModernizedActivityEntityType.Assignment,
    status: assignment.status,
    pendingDelete: assignment.pendingDelete ?? false,
    removalRecommended: assignment.removalRecommended ?? undefined,
    removalReason: assignment.removalReason ?? undefined,
    externalVisibility: assignment.externalVisibility ?? undefined,
    meta: buildMeta({
      itemType,
      isNew: assignment.isNew,
      isManuallyAuthored,
      qualityIndicator: assignment.qualityIndicator,
      tag,
    }),
  });

  details[assignment.id] = {
    id: assignment.id,
    isManuallyAuthored,
    currentTitle: assignment.title ?? "",
    currentDescription: firstNonBlankDescription(assignment.descriptionText, assignment.descriptionHtml),
    recommendedTitle: assignment.recommendedTitle ?? assignment.title ?? "",
    recommendedDescription:
      assignment.recommendedDescription ??
      recommendationSummary.recommendedDescription ??
      firstNonBlankDescription(assignment.descriptionText, assignment.descriptionHtml),
    activityType: assignment.gradingType ?? undefined,
    suggestedContentType: assignment.isNew ? itemType : undefined,
    qualityLabel: assignment.qualityIndicator ?? undefined,
    pointsPossible: assignment.pointsPossible ?? undefined,
    recommendedPointsPossible: assignment.recommendedPointsPossible ?? undefined,
    dueDate: assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString("en-US") : undefined,
    recommendedDueDate: assignment.recommendedDueDate
      ? new Date(assignment.recommendedDueDate).toLocaleDateString("en-US")
      : undefined,
    recommendedDueDateCleared: assignment.recommendedDueDateCleared ?? undefined,
    url: assignment.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    issueFindings: issueSummary.issueFindings,
    issueFindingsDetailed: issueSummary.issueFindingsDetailed,
    recommendationSources: recommendationSummary.recommendationSources,
    documentReference: `Content > ${module.title ?? "Module"} > ${assignment.title ?? "Activity"}`,
    contentCategory: tag,
    attachments: mapGeneratedAssetsToAttachments(assignment.generatedAssets, assignment.originalFiles),
    ...lastModified,
  };
}

function addAssessmentItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  assessment: ReviewAssessment
): void {
  const recommendationSummary = getRecommendationSummary(assessment.recommendations);
  const lastModified = getLastModified(assessment.editLogs);
  const recommendedTitle =
    getLatestEditLogAfterValue(assessment.editLogs, EditableValue.RecommendedTitle) ??
    assessment.recommendedTitle ??
    assessment.title ??
    "";
  const recommendedDescription =
    getLatestEditLogAfterValue(assessment.editLogs, EditableValue.RecommendedDescription) ??
    assessment.recommendedDescription ??
    recommendationSummary.recommendedDescription ??
    firstNonBlankDescription(assessment.descriptionText, assessment.descriptionHtml);
  const issueSummary = getSuggestedItemIssueFindings(
    module,
    assessment.isNew,
    buildSuggestedIssueContext([
      "quiz assessment",
      assessment.title,
      recommendedTitle,
      buildSuggestedInstructionalContext([
        recommendedDescription,
        firstNonBlankDescription(assessment.descriptionText, assessment.descriptionHtml),
      ]),
      getRecommendationContext(recommendationSummary),
    ]),
    assessment.issueFindings,
    assessment.issueFindingDimensions,
    assessment.issueFindingDimensionLabels
  );

  items.push({
    id: assessment.id,
    title: assessment.title ?? "Untitled Assessment",
    kind: assessment.isNew ? "suggestedContent" : "assignment",
    entityType: ModernizedActivityEntityType.Assessment,
    status: assessment.status,
    pendingDelete: assessment.pendingDelete ?? false,
    removalRecommended: assessment.removalRecommended ?? undefined,
    removalReason: assessment.removalReason ?? undefined,
    externalVisibility: assessment.externalVisibility ?? undefined,
    meta: buildMeta({ itemType: "quiz", isNew: assessment.isNew, qualityIndicator: assessment.qualityIndicator }),
  });

  details[assessment.id] = {
    id: assessment.id,
    currentTitle: assessment.title ?? "",
    currentDescription: firstNonBlankDescription(assessment.descriptionText, assessment.descriptionHtml),
    recommendedTitle,
    recommendedDescription,
    activityType: "quiz",
    suggestedContentType: assessment.isNew ? "quiz" : undefined,
    qualityLabel: assessment.qualityIndicator ?? undefined,
    pointsPossible: assessment.pointsPossible ?? undefined,
    recommendedPointsPossible: assessment.recommendedPointsPossible ?? undefined,
    dueDate: assessment.dueDate ? new Date(assessment.dueDate).toLocaleDateString("en-US") : undefined,
    recommendedDueDate: assessment.recommendedDueDate
      ? new Date(assessment.recommendedDueDate).toLocaleDateString("en-US")
      : undefined,
    recommendedDueDateCleared: assessment.recommendedDueDateCleared ?? undefined,
    url: assessment.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    issueFindings: issueSummary.issueFindings,
    issueFindingsDetailed: issueSummary.issueFindingsDetailed,
    recommendationSources: recommendationSummary.recommendationSources,
    documentReference: `Content > ${module.title ?? "Module"} > ${assessment.title ?? "Assessment"}`,
    attachments: mapGeneratedAssetsToAttachments(assessment.generatedAssets, assessment.originalFiles),
    ...lastModified,
  };
}

function addProjectItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  project: ReviewProject
): void {
  const recommendationSummary = getRecommendationSummary(project.recommendations);
  const lastModified = getLastModified(project.editLogs);
  const isManuallyAuthored = isManuallyAuthoredActivity(project.creationSource);
  const issueSummary = getSuggestedItemIssueFindings(
    module,
    project.isNew,
    buildSuggestedIssueContext([
      "project",
      project.title,
      project.recommendedTitle,
      buildSuggestedInstructionalContext([
        project.recommendedDescription,
        firstNonBlankDescription(project.descriptionText, project.descriptionHtml),
      ]),
      getRecommendationContext(recommendationSummary),
    ]),
    project.issueFindings,
    project.issueFindingDimensions,
    project.issueFindingDimensionLabels
  );

  items.push({
    id: project.id,
    title: project.title ?? "Untitled Project",
    kind: project.isNew ? "suggestedContent" : "assignment",
    entityType: ModernizedActivityEntityType.Project,
    status: project.status,
    pendingDelete: project.pendingDelete ?? false,
    removalRecommended: project.removalRecommended ?? undefined,
    removalReason: project.removalReason ?? undefined,
    externalVisibility: project.externalVisibility ?? undefined,
    meta: buildMeta({
      itemType: "project",
      isNew: project.isNew,
      isManuallyAuthored,
      qualityIndicator: project.qualityIndicator,
    }),
  });

  details[project.id] = {
    id: project.id,
    isManuallyAuthored,
    currentTitle: project.title ?? "",
    currentDescription: firstNonBlankDescription(project.descriptionText, project.descriptionHtml),
    recommendedTitle: project.recommendedTitle ?? project.title ?? "",
    recommendedDescription: project.recommendedDescription ?? recommendationSummary.recommendedDescription ?? "",
    activityType: "html-content",
    qualityLabel: project.qualityIndicator ?? undefined,
    dueDate: project.dueDate ? new Date(project.dueDate).toLocaleDateString("en-US") : undefined,
    recommendedDueDate: project.recommendedDueDate
      ? new Date(project.recommendedDueDate).toLocaleDateString("en-US")
      : undefined,
    recommendedDueDateCleared: project.recommendedDueDateCleared ?? undefined,
    url: project.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    issueFindings: issueSummary.issueFindings,
    issueFindingsDetailed: issueSummary.issueFindingsDetailed,
    recommendationSources: recommendationSummary.recommendationSources,
    documentReference: `Content > ${module.title ?? "Module"} > ${project.title ?? "Project"}`,
    attachments: mapGeneratedAssetsToAttachments(project.generatedAssets, project.originalFiles),
    ...lastModified,
  };
}

function addAxioResourceItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  resource: ReviewAxioResource
): void {
  const recommendationSummary = getRecommendationSummary(resource.recommendations);
  const lastModified = getLastModified(resource.editLogs);
  const currentDescription = resource.descriptionText ?? resource.descriptionHtml ?? "";
  const issueSummary = getSuggestedItemIssueFindings(
    module,
    resource.isNew,
    buildSuggestedIssueContext([
      "resource",
      resource.title,
      resource.recommendedTitle,
      resource.recommendedDescription,
      currentDescription,
      getRecommendationContext(recommendationSummary),
    ])
  );

  items.push({
    id: resource.id,
    title: resource.title ?? "Untitled Resource",
    kind: "suggestedContent",
    entityType: ModernizedActivityEntityType.AxioResource,
    status: resource.status,
    pendingDelete: resource.pendingDelete ?? false,
    removalRecommended: resource.removalRecommended ?? undefined,
    removalReason: resource.removalReason ?? undefined,
    externalVisibility: resource.externalVisibility ?? undefined,
    meta: buildMeta({ itemType: "resource", isNew: resource.isNew }),
  });

  details[resource.id] = {
    id: resource.id,
    currentTitle: resource.title ?? "",
    currentDescription,
    recommendedTitle: resource.recommendedTitle ?? resource.title ?? "",
    recommendedDescription: resource.recommendedDescription ?? currentDescription,
    activityType: "resource",
    suggestedContentType: "resource",
    url: resource.externalUrl ?? resource.videoUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    recommendationSources: recommendationSummary.recommendationSources,
    issueFindings: issueSummary.issueFindings,
    issueFindingsDetailed: issueSummary.issueFindingsDetailed,
    documentReference: `Content > ${module.title ?? "Module"}`,
    attachments: mapGeneratedAssetsToAttachments(resource.generatedAssets, resource.originalFiles),
    ...lastModified,
  };
}

function addOtherActivityItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  activity: ReviewOtherActivity
): void {
  const recommendationSummary = getRecommendationSummary(activity.recommendations);
  const lastModified = getLastModified(activity.editLogs);
  // A "webpage" activity with no HTML body but a non-empty external URL was
  // misclassified during import (e.g. a Brightspace URL topic whose URL happened
  // to end in .html).  Treat it as a link so it renders in ExternalLinkPanel.
  const effectiveActivityType =
    activity.activityType === "webpage" && !activity.descriptionHtml && Boolean(activity.externalUrl)
      ? "link"
      : activity.activityType;
  const isPageLikeActivity = effectiveActivityType === "page" || effectiveActivityType === "webpage";
  const isManuallyAuthored = isManuallyAuthoredActivity(activity.creationSource);
  const itemType =
    isManuallyAuthored && isPageLikeActivity
      ? "page"
      : effectiveActivityType === "page"
        ? "resource"
        : mapActivityTypeToItemType(effectiveActivityType);
  const isSuggested = effectiveActivityType === "ile" || isPageLikeActivity || Boolean(activity.isNew);
  const kind: CourseEvaluatorReviewItemKind = isSuggested ? "suggestedContent" : "assignment";
  const isWebpage = isPageLikeActivity;
  // For webpage activities the body is raw HTML — always prefer descriptionHtml.
  // For other types (quiz, discussion, etc.) use descriptionText where available.
  // Use || not ?? so an empty string falls through (Brightspace quiz descriptions
  // are often HTML-only with descriptionText stored as "").
  // If we only have descriptionHtml for a non-webpage, strip tags so the
  // plain-text diff view shows readable content instead of raw markup.
  const rawCurrentHtml = activity.descriptionHtml ?? "";
  const rawCurrentText = activity.descriptionText ?? "";
  const strippedHtml = rawCurrentHtml
    .replace(/<[^>]*>/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
  const currentDescription = isWebpage ? rawCurrentHtml || rawCurrentText : rawCurrentText || strippedHtml;
  // For webpages, only use recommendedDescription if it contains HTML tags — meaning
  // the reviewer explicitly edited the HTML source.  The AI planner sets
  // recommendedDescription to plain text for webpages, which would strip all
  // embedded media/links if pushed as the file body.  Always fall back to the
  // original descriptionHtml so the file content is preserved by default.
  // For webpages, prefer any explicitly saved recommendedDescription (even plain
  // text) over the AI-generated one, which may strip embedded media/links.
  // We detect an explicit user edit by checking whether the saved value differs
  // from the current descriptionHtml — if they are identical the reviewer has
  // not changed anything and we fall back to descriptionHtml.
  const savedRec = activity.recommendedDescription;
  const isExplicitWebpageEdit = isWebpage && savedRec != null && savedRec !== activity.descriptionHtml;
  const webpageRec = isExplicitWebpageEdit ? savedRec : undefined;
  const nonWebpageRec = activity.recommendedDescription ?? recommendationSummary.recommendedDescription;
  const aiOrUserRecommendedDescription = isWebpage ? webpageRec : nonWebpageRec;
  const recommendedDescription = isWebpage
    ? (aiOrUserRecommendedDescription ?? activity.descriptionHtml ?? "")
    : (aiOrUserRecommendedDescription ?? "");
  const issueSummary = getSuggestedItemIssueFindings(
    module,
    activity.isNew,
    buildSuggestedIssueContext([
      itemType,
      activity.activityType,
      activity.title,
      activity.recommendedTitle,
      itemType === "resource"
        ? buildSuggestedIssueContext([recommendedDescription, currentDescription])
        : buildSuggestedInstructionalContext([recommendedDescription, currentDescription]),
      getRecommendationContext(recommendationSummary),
    ]),
    activity.issueFindings,
    activity.issueFindingDimensions,
    activity.issueFindingDimensionLabels
  );

  items.push({
    id: activity.id,
    title: activity.title ?? module.title ?? "Untitled Activity",
    kind,
    entityType: ModernizedActivityEntityType.OtherActivity,
    status: activity.status,
    pendingDelete: activity.pendingDelete ?? false,
    removalRecommended: activity.removalRecommended ?? undefined,
    removalReason: activity.removalReason ?? undefined,
    externalVisibility: activity.externalVisibility ?? undefined,
    meta: buildMeta({
      itemType,
      isNew: activity.isNew,
      isManuallyAuthored,
      qualityIndicator: activity.qualityIndicator,
    }),
  });

  details[activity.id] = {
    id: activity.id,
    isManuallyAuthored,
    currentTitle: activity.title ?? "",
    currentDescription,
    recommendedTitle: activity.recommendedTitle ?? activity.title ?? "",
    recommendedDescription,
    activityType: activity.activityType ?? undefined,
    suggestedContentType: isSuggested ? itemType : undefined,
    qualityLabel: activity.qualityIndicator ?? undefined,
    dueDate: activity.dueDate ? new Date(activity.dueDate).toLocaleDateString("en-US") : undefined,
    recommendedDueDate: activity.recommendedDueDate
      ? new Date(activity.recommendedDueDate).toLocaleDateString("en-US")
      : undefined,
    recommendedDueDateCleared: activity.recommendedDueDateCleared ?? undefined,
    url: activity.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    issueFindings: issueSummary.issueFindings,
    issueFindingsDetailed: issueSummary.issueFindingsDetailed,
    recommendationSources: recommendationSummary.recommendationSources,
    learningObjective: activity.activityType === "ile" ? (recommendationSummary.feedback ?? undefined) : undefined,
    documentReference: `Content > ${module.title ?? "Module"}`,
    attachments: mapGeneratedAssetsToAttachments(activity.generatedAssets, activity.originalFiles),
    ...lastModified,
  };
}

function addIleRecommendationItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  ileRecommendation: ReviewIleRecommendation
): void {
  const recommendationSummary = getRecommendationSummary(ileRecommendation.recommendations);
  const lastModified = getLastModified(ileRecommendation.editLogs);
  const isUserCreatedIle = ileRecommendation.editLogs.some(
    (editLog) =>
      editLog.fieldName === EditableValue.IleRecommendation &&
      Boolean(ileRecommendation.aiTutor?.id) &&
      editLog.afterValue === ileRecommendation.aiTutor?.id
  );
  const isManuallyAuthored = isManuallyAuthoredActivity(ileRecommendation.creationSource);
  const isNewIle = !ileRecommendation.aiTutor?.id || isUserCreatedIle;
  const issueSummary = getSuggestedItemIssueFindings(
    module,
    isNewIle,
    buildSuggestedIssueContext([
      "ile interactive learning experience lab practice",
      ileRecommendation.title,
      buildSuggestedInstructionalContext([ileRecommendation.description]),
      getRecommendationContext(recommendationSummary),
    ])
  );
  const createdIleObjectives = ileRecommendation.aiTutor?.objectives
    .filter((objective): objective is ReviewIleObjective => Boolean(objective?.objective))
    .map((objective) => ({
      id: objective.id,
      objective: objective.objective ?? "",
      sequence: objective.sequence,
      evalCriteria: (objective.evalCriteria ?? []).filter((criteria): criteria is string => Boolean(criteria)),
    }));

  items.push({
    id: ileRecommendation.id,
    title: ileRecommendation.title ?? module.title ?? "Untitled Activity",
    kind: "suggestedContent",
    entityType: ModernizedActivityEntityType.IleRecommendation,
    status: ileRecommendation.status,
    pendingDelete: ileRecommendation.pendingDelete ?? false,
    meta: buildMeta({ itemType: "ile", isNew: isNewIle, isManuallyAuthored }),
  });

  details[ileRecommendation.id] = {
    id: ileRecommendation.id,
    isManuallyAuthored,
    currentTitle: ileRecommendation.title ?? "",
    currentDescription: ileRecommendation.description ?? "",
    recommendedTitle: ileRecommendation.title ?? "",
    recommendedDescription: recommendationSummary.recommendedDescription ?? ileRecommendation.description ?? "",
    activityType: "ile",
    suggestedContentType: "ile",
    feedback: recommendationSummary.feedback,
    recommendationSources: recommendationSummary.recommendationSources,
    issueFindings: issueSummary.issueFindings,
    issueFindingsDetailed: issueSummary.issueFindingsDetailed,
    learningObjective: recommendationSummary.feedback ?? undefined,
    documentReference: `Content > ${module.title ?? "Module"}`,
    createdIleId: ileRecommendation.aiTutor?.id,
    createdIleWasUserCreated: isUserCreatedIle,
    createdIleTitle: ileRecommendation.aiTutor?.title,
    createdIleDescription: ileRecommendation.aiTutor?.description,
    createdIleContext: ileRecommendation.aiTutor?.context,
    createdIleDueDate: ileRecommendation.aiTutor?.dueDate,
    createdIleTargetAudience: ileRecommendation.aiTutor?.targetAudience,
    createdIleActivityState: ileRecommendation.aiTutor?.activityState,
    createdIleScheduledDate: ileRecommendation.aiTutor?.scheduledDate,
    createdIleObjectives,
    ...lastModified,
  };
}

function hasReviewText(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

function isPublishedPendingDelete(item: {
  pendingDelete?: boolean | null;
  status?: ModernizedActivityStatus | null;
}): boolean {
  return item.pendingDelete === true && item.status === ModernizedActivityStatus.Published;
}

function isValidIleRecommendation(ileRecommendation: ReviewIleRecommendation): boolean {
  const recommendationSummary = getRecommendationSummary(ileRecommendation.recommendations);
  return (
    hasReviewText(ileRecommendation.title) &&
    (hasReviewText(ileRecommendation.description) ||
      hasReviewText(recommendationSummary.recommendedDescription) ||
      hasReviewText(recommendationSummary.feedback) ||
      Boolean(ileRecommendation.aiTutor?.id))
  );
}

function sortItemsByActivityOrder<T extends { id: string }>(
  items: T[],
  activityOrder: string[] | null | undefined
): T[] {
  if (!activityOrder || activityOrder.length === 0) {
    return items;
  }

  const orderSet = new Set(activityOrder);
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const orderedItems: T[] = [];
  const seen = new Set<string>();
  for (const id of activityOrder) {
    if (seen.has(id)) continue;
    seen.add(id);
    const item = itemsById.get(id);
    if (item) orderedItems.push(item);
  }
  const unorderedItems = items.filter((item) => !orderSet.has(item.id));

  return [...orderedItems, ...unorderedItems];
}

function buildModuleGroups(
  modernizedCourse: ReviewCourse,
  details: Record<string, ActivityDetail>
): CourseEvaluatorReviewSidebarGroup[] {
  const firstVisibleModuleId = modernizedCourse.modules.find((module) => !isPublishedPendingDelete(module))?.id;

  return modernizedCourse.modules.flatMap((module) => {
    if (isPublishedPendingDelete(module)) {
      return [];
    }

    const items: CourseEvaluatorReviewSidebarItem[] = [];
    const visibleOtherActivities = module.otherActivities.filter(
      (activity) => activity.activityType?.toLowerCase() !== "ile" && !isPublishedPendingDelete(activity)
    );
    const visibleIleRecommendations = module.ileRecommendations.filter(
      (ileRecommendation) => isValidIleRecommendation(ileRecommendation) && !isPublishedPendingDelete(ileRecommendation)
    );

    if (shouldAddModuleDescriptionItem(module, visibleOtherActivities, visibleIleRecommendations)) {
      addModuleDescriptionItem(details, items, module);
    }

    type PendingModuleItem = {
      id: string;
      add: () => void;
    };

    const pendingItems: PendingModuleItem[] = [];

    module.assignments
      .filter((assignment) => !isPublishedPendingDelete(assignment))
      .forEach((assignment) => {
        pendingItems.push({
          id: assignment.id,
          add: () => addAssignmentItem(details, items, module, assignment),
        });
      });
    module.assessments
      .filter((assessment) => !isPublishedPendingDelete(assessment))
      .forEach((assessment) => {
        pendingItems.push({
          id: assessment.id,
          add: () => addAssessmentItem(details, items, module, assessment),
        });
      });
    module.axioResources
      .filter((resource) => !isPublishedPendingDelete(resource))
      .forEach((resource) => {
        pendingItems.push({
          id: resource.id,
          add: () => addAxioResourceItem(details, items, module, resource),
        });
      });
    module.projects
      .filter((project) => !isPublishedPendingDelete(project))
      .forEach((project) => {
        pendingItems.push({
          id: project.id,
          add: () => addProjectItem(details, items, module, project),
        });
      });
    module.otherActivities
      .filter((activity) => activity.activityType?.toLowerCase() !== "ile" && !isPublishedPendingDelete(activity))
      .forEach((activity) => {
        pendingItems.push({
          id: activity.id,
          add: () => addOtherActivityItem(details, items, module, activity),
        });
      });
    visibleIleRecommendations.forEach((ile) => {
      pendingItems.push({
        id: ile.id,
        add: () => addIleRecommendationItem(details, items, module, ile),
      });
    });

    for (const pendingItem of sortItemsByActivityOrder(pendingItems, module.activityOrder)) {
      pendingItem.add();
    }

    return [
      {
        id: module.id,
        title: module.title ?? "Untitled Module",
        decided: 0,
        total: items.length,
        isNew: module.isNew ?? false,
        pendingDelete: module.pendingDelete ?? false,
        defaultExpanded: firstVisibleModuleId === module.id,
        externalUrl: module.externalUrl ?? undefined,
        estimatedMinutes: module.estimatedMinutes ?? undefined,
        removalRecommended: module.removalRecommended ?? undefined,
        removalReason: module.removalReason ?? undefined,
        addRecommended: module.addRecommended ?? undefined,
        items,
      },
    ];
  });
}

function buildCourseLevelGroup(
  modernizedCourse: ReviewCourse,
  details: Record<string, ActivityDetail>
): CourseEvaluatorReviewSidebarGroup {
  const items: CourseEvaluatorReviewSidebarItem[] = modernizedCourse.contentSections.map((section) => {
    addContentSectionDetail(details, section);
    return {
      id: section.id,
      title: CONTENT_SECTION_TITLES[section.type] ?? section.type,
      kind: "recommendation",
      entityType: ModernizedActivityEntityType.ContentSection,
      status: section.status,
      meta: section.isNew ? [{ kind: "tag", tag: "new" }] : undefined,
    };
  });

  return {
    id: "course-level-content",
    title: "Course-Level Content",
    decided: 0,
    total: items.length,
    defaultExpanded: true,
    items,
  };
}

function mapModernizerUser(user: ReviewModernizerUser | null | undefined): CourseModernizerReviewUser | null {
  if (!user?.id) {
    return null;
  }

  return {
    id: user.id,
    name: user.name || user.email || "Unnamed user",
    email: user.email ?? "",
  };
}

function toTeamManagement(modernizedCourse: ReviewCourse): CourseModernizerReviewTeamManagement | null {
  const intake = modernizedCourse.modernizerIntake;
  const architect = mapModernizerUser(intake?.architect);
  const editingTeam =
    intake?.editingTeamConnection.edges.flatMap((edge) => {
      const user = mapModernizerUser(edge.node);
      return user ? [{ user, role: edge.properties.role }] : [];
    }) ?? [];

  if (!architect && editingTeam.length === 0) {
    return null;
  }

  return {
    architect,
    editingTeam,
    schoolId: modernizedCourse.externalCourse?.school?.id ?? null,
  };
}

export function mapModernizedCourseToReviewData(
  modernizedCourse: ReviewCourse,
  rubricName?: string | null
): ReviewDataBundle {
  const activityDetails: Record<string, ActivityDetail> = {};
  const courseLevelGroup = buildCourseLevelGroup(modernizedCourse, activityDetails);
  const moduleGroups = buildModuleGroups(modernizedCourse, activityDetails);
  const sidebarGroups = [courseLevelGroup, ...moduleGroups];
  const totalCount = sidebarGroups.reduce((sum, group) => sum + group.total, 0);
  const allItems = sidebarGroups.flatMap((group) => group.items);
  const queuedCount = allItems.filter((item) => item.status === ModernizedActivityStatus.Queued).length;
  const skippedCount = allItems.filter((item) => item.status === ModernizedActivityStatus.Skipped).length;
  const publishedCount = allItems.filter((item) => item.status === ModernizedActivityStatus.Published).length;
  const reviewedCount = queuedCount + skippedCount + publishedCount;

  return {
    review: {
      courseId: modernizedCourse.id,
      courseName: modernizedCourse.title ?? "",
      sourceLabel: modernizedCourse.externalSource ? SOURCE_LABELS[modernizedCourse.externalSource] : "Unknown",
      targetAudience: resolveCourseTargetAudience(modernizedCourse),
      canPublishToLms:
        modernizedCourse.externalSource === ExternalSourceSystem.Canvas ||
        modernizedCourse.externalSource === ExternalSourceSystem.Brightspace,
      canMoveActivitiesAcrossModules: modernizedCourse.externalSource !== ExternalSourceSystem.Brightspace,
      courseCode: modernizedCourse.courseCode ?? "",
      termLabel: modernizedCourse.term ?? "",
      frameworkName: rubricName || "Rubric Alignment",
      reviewStage: "full-course-review",
      effectiveness: modernizedCourse.qualityRating ? QUALITY_TO_EFFECTIVENESS[modernizedCourse.qualityRating] : "good",
      reviewedDateLabel: modernizedCourse.modules[0]?.lastModified
        ? new Date(modernizedCourse.modules[0].lastModified).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "",
      score: modernizedCourse.totalScore ?? 0,
      maxScore: modernizedCourse.totalMaxScore ?? 50,
      reviewedCount,
      totalCount,
      queuedCount,
      skippedCount,
      publishedCount,
      categoryBreakdown: toCategoryBreakdown(modernizedCourse),
      evaluatorDimensionScores: toEvaluatorDimensionScores(modernizedCourse),
      strengths: [...modernizedCourse.strengths],
      improvements: [...modernizedCourse.itemsToImprove],
      documentIssues: toDocumentIssues(modernizedCourse),
      documentUpdates: toDocumentUpdates(modernizedCourse),
      sidebarGroups,
      teamManagement: toTeamManagement(modernizedCourse),
    },
    activityDetails,
  };
}
